import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Settings, Trash2, ShieldCheck, AlertTriangle, ChevronRight, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function AdobeLicenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [data, setData] = useState({ configured: false, products: [], total_products: 0, total_assignments: 0 });
  const [settings, setSettings] = useState({ ims_org_id: "", client_id: "", configured: false, last_synced_at: null, last_sync_status: null, last_sync_error: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({ ims_org_id: "", client_id: "", client_secret: "" });

  // Drill-down
  const [drillProduct, setDrillProduct] = useState(null);
  const [drillUsers, setDrillUsers] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/adobe/settings"),
        api.get("/adobe/licenses"),
      ]);
      setSettings(s.data);
      setData(l.data);
      setForm({ ims_org_id: s.data.ims_org_id || "", client_id: s.data.client_id || "", client_secret: "" });
    } catch (e) {
      toast.error("Failed to load Adobe license data");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, []);

  const onSync = async () => {
    setSyncing(true);
    try {
      const { data: result } = await api.post("/adobe/sync");
      toast.success(`Synced · ${result.product_count} product${result.product_count === 1 ? "" : "s"} · ${result.user_count} user${result.user_count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      await load();
    } finally { setSyncing(false); }
  };

  const onSaveSettings = async () => {
    const ims_org_id = form.ims_org_id.trim();
    const client_id = form.client_id.trim();
    const client_secret = form.client_secret.trim();
    if (!ims_org_id || !client_id) { toast.error("IMS Org ID and Client ID are required"); return; }
    if (!settings.configured && !client_secret) { toast.error("Client Secret is required for initial setup"); return; }
    try {
      const payload = { ims_org_id, client_id };
      if (client_secret) payload.client_secret = client_secret;
      await api.put("/adobe/settings", payload);
      toast.success("Adobe credentials saved");
      setSettingsOpen(false);
      setForm({ ...form, client_secret: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save settings"); }
  };

  const onDeleteSettings = async () => {
    try {
      await api.delete("/adobe/settings");
      toast.success("Adobe credentials removed");
      setConfirmDelete(false);
      await load();
    } catch { toast.error("Failed to remove credentials"); }
  };

  const openDrillDown = async (productName) => {
    setDrillProduct(productName);
    setDrillUsers([]);
    setDrillLoading(true);
    try {
      const { data: r } = await api.get(`/adobe/products/${encodeURIComponent(productName)}/users`);
      setDrillUsers(r.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
      setDrillProduct(null);
    } finally { setDrillLoading(false); }
  };

  if (!isAdmin) {
    return (
      <div data-testid="adobe-page" className="p-6">
        <PageHeader eyebrow="[08] Licensing" title="Adobe Licenses" subtitle="Restricted to admins." />
        <div className="p-6 font-mono text-xs text-[#7A7A7A]">You don&apos;t have permission to view this page.</div>
      </div>
    );
  }

  const lastSyncBad = settings.last_sync_status === "failed";

  return (
    <div data-testid="adobe-page">
      <PageHeader
        testid="adobe-header"
        eyebrow="[08] Licensing"
        title="Adobe Licenses"
        subtitle="Snapshot of Adobe Admin Console product assignments via the User Management API."
        actions={
          <>
            <Button
              variant="outline"
              data-testid="adobe-settings-btn"
              onClick={() => setSettingsOpen(true)}
              className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
            >
              <Settings className="w-3.5 h-3.5 mr-2" />{settings.configured ? "Connection" : "Connect"}
            </Button>
            <Button
              data-testid="adobe-sync-btn"
              onClick={onSync}
              disabled={!settings.configured || syncing}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Now"}
            </Button>
          </>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Status" value={
            settings.configured
              ? (lastSyncBad ? "Sync error" : (settings.last_sync_status === "success" ? "Connected" : "Configured"))
              : "Not connected"
          } tone={settings.configured ? (lastSyncBad ? "warn" : "ok") : "muted"} testid="kpi-adobe-status" />
          <Kpi label="Last Sync" value={formatTimestamp(settings.last_synced_at)} testid="kpi-adobe-last-sync" />
          <Kpi label="Products" value={data.total_products?.toLocaleString?.() ?? "—"} testid="kpi-adobe-products" />
          <Kpi label="Total Assignments" value={data.total_assignments?.toLocaleString?.() ?? "—"} testid="kpi-adobe-assignments" />
        </div>

        {lastSyncBad && settings.last_sync_error && (
          <div data-testid="adobe-sync-error" className="border border-[#E0B8A5] bg-[#FFF4EE] px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-[#B45309] mt-0.5" strokeWidth={1.75} />
            <div className="flex-1 min-w-0">
              <div className="uppercase-label text-[#B45309]">Last sync failed</div>
              <div className="font-mono text-xs text-[#3A3A3A] mt-1 break-words">{settings.last_sync_error}</div>
            </div>
          </div>
        )}

        {!settings.configured && (
          <div data-testid="adobe-empty-state" className="border border-[#E5E0D5] bg-white px-6 py-10 text-center space-y-4">
            <ShieldCheck className="w-8 h-8 text-[#5A5A5A] mx-auto" strokeWidth={1.5} />
            <div className="space-y-1">
              <div className="text-[#0F0F0F] font-medium">Connect Adobe Admin Console</div>
              <div className="text-sm text-[#5A5A5A] max-w-md mx-auto">
                In <span className="font-mono">developer.adobe.com/console</span> create a project → add the User Management API → choose <strong>OAuth Server-to-Server</strong> credentials → assign relevant product profiles. Then paste the IMS Org ID, Client ID, and Client Secret below.
              </div>
            </div>
            <Button
              data-testid="adobe-empty-connect-btn"
              onClick={() => setSettingsOpen(true)}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Settings className="w-3.5 h-3.5 mr-2" />Connect Adobe
            </Button>
            <a
              href="https://developer.adobe.com/developer-console/docs/guides/services/services-add-api-oauth-s2s"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-mono text-[#5A5A5A] hover:text-[#0F0F0F] underline-offset-2 hover:underline"
            >
              Adobe OAuth S2S guide <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {settings.configured && (
          <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="adobe-products-table">
              <thead>
                <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                  <Th>Product</Th>
                  <Th align="right">Profiles</Th>
                  <Th align="right">Assigned Users</Th>
                  <Th>&nbsp;</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>
                )}
                {!loading && data.products.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-12 text-center">
                    <div className="font-mono text-xs text-[#7A7A7A]">No data cached. Click <strong>Sync Now</strong> to pull from Adobe.</div>
                  </td></tr>
                )}
                {data.products.map((p) => (
                  <tr
                    key={p.product_name}
                    data-testid={`adobe-product-row-${p.product_name}`}
                    onClick={() => openDrillDown(p.product_name)}
                    className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3] transition-colors cursor-pointer"
                  >
                    <Td><span className="text-[#0F0F0F]">{p.product_name}</span></Td>
                    <Td align="right"><span className="font-mono text-xs text-[#3A3A3A]">{p.profile_count}</span></Td>
                    <Td align="right"><span className="font-mono text-xs text-[#3A3A3A]">{p.assigned_count.toLocaleString()}</span></Td>
                    <Td>
                      <ChevronRight className="w-4 h-4 text-[#9A9A9A] ml-auto" />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {settings.configured && (
          <div className="font-mono text-xs text-[#9A9A9A] flex items-center justify-between">
            <span>Total products: {data.products.length} · Synced: {formatTimestamp(settings.last_synced_at)}</span>
            <button
              onClick={() => setConfirmDelete(true)}
              data-testid="adobe-disconnect-btn"
              className="text-[#7A7A7A] hover:text-[#B91C1C] inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3 h-3" /> Disconnect Adobe tenant
            </button>
          </div>
        )}
      </div>

      {/* Drill-down panel */}
      {drillProduct && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end"
          onClick={() => setDrillProduct(null)}
          data-testid="adobe-drilldown-backdrop"
        >
          <div
            className="bg-white w-full max-w-2xl h-full overflow-y-auto border-l border-[#D6CFC1]"
            onClick={(e) => e.stopPropagation()}
            data-testid="adobe-drilldown-panel"
          >
            <div className="px-6 py-4 border-b border-[#E5E0D5] flex items-center justify-between">
              <div>
                <div className="uppercase-label">Product</div>
                <div className="text-lg font-medium text-[#0F0F0F]">{drillProduct}</div>
              </div>
              <button onClick={() => setDrillProduct(null)} data-testid="adobe-drilldown-close" className="p-1 text-[#5A5A5A] hover:text-[#0F0F0F]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {drillLoading && <div className="font-mono text-xs text-[#7A7A7A]">Loading users…</div>}
              {!drillLoading && drillUsers.length === 0 && (
                <div className="font-mono text-xs text-[#7A7A7A]">No users assigned to this product.</div>
              )}
              {!drillLoading && drillUsers.length > 0 && (
                <div className="border border-[#E5E0D5] overflow-hidden">
                  <table className="w-full text-sm" data-testid="adobe-drilldown-users">
                    <thead>
                      <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th>Profile(s)</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillUsers.map((u) => (
                        <tr key={u.email} className="border-b border-[#E5E0D5]">
                          <Td>
                            <span className="text-[#0F0F0F]">
                              {(u.firstname || u.lastname) ? `${u.firstname} ${u.lastname}`.trim() : "—"}
                            </span>
                          </Td>
                          <Td><span className="font-mono text-xs text-[#3A3A3A]">{u.email}</span></Td>
                          <Td>
                            <div className="flex flex-wrap gap-1">
                              {(u.profiles || []).map((pp) => (
                                <span key={pp} className="font-mono text-[10px] px-1.5 py-0.5 border border-[#E5E0D5] bg-[#FAF7F0] text-[#5A5A5A]">{pp}</span>
                              ))}
                            </div>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="font-mono text-xs text-[#9A9A9A]">{drillUsers.length} user{drillUsers.length === 1 ? "" : "s"} assigned</div>
            </div>
          </div>
        </div>
      )}

      {/* Connection modal */}
      <AlertDialog open={settingsOpen} onOpenChange={(o) => !o && setSettingsOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Adobe Admin Console Connection</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              Credentials are stored encrypted and used only to read user/license data from the User Management API.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="uppercase-label">IMS Org ID</Label>
              <Input
                data-testid="adobe-org-id-input"
                value={form.ims_org_id}
                onChange={(e) => setForm({ ...form, ims_org_id: e.target.value })}
                placeholder="12345ABC@AdobeOrg"
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm mt-1"
              />
            </div>
            <div>
              <Label className="uppercase-label">Client ID (API Key)</Label>
              <Input
                data-testid="adobe-client-id-input"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="32-char hex string"
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm mt-1"
              />
            </div>
            <div>
              <Label className="uppercase-label">Client Secret {settings.configured && <span className="ml-1 normal-case tracking-normal text-[#7A7A7A]">(leave blank to keep existing)</span>}</Label>
              <Input
                data-testid="adobe-client-secret-input"
                type="password"
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder={settings.configured ? "•••••••••••••" : "Client Secret from Developer Console"}
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm mt-1"
              />
            </div>
            <div className="text-xs text-[#7A7A7A] font-mono leading-relaxed border border-[#E5E0D5] bg-[#FAF7F0] px-3 py-2">
              <strong>Setup tip:</strong> in Adobe Developer Console add the <strong>User Management API</strong> with <strong>OAuth Server-to-Server</strong> credentials, then assign the credential the product profiles whose users/licenses you want to read.
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="adobe-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="adobe-save-btn" onClick={onSaveSettings} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disconnect confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Disconnect Adobe tenant?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">Removes the stored credentials and cached license data for this organization. You can reconnect any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="adobe-confirm-disconnect-btn" onClick={onDeleteSettings} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-${align} font-medium`}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td className={`px-4 py-3 text-${align}`}>{children}</td>;
}
function Kpi({ label, value, tone = "default", testid }) {
  const valueClass =
    tone === "ok" ? "text-[#2D7A4F]"
    : tone === "warn" ? "text-[#B45309]"
    : tone === "muted" ? "text-[#9A9A9A]"
    : "text-[#0F0F0F]";
  return (
    <div data-testid={testid} className="border border-[#E5E0D5] bg-white px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">{label}</div>
      <div className={`mt-1 font-mono text-lg ${valueClass}`}>{value}</div>
    </div>
  );
}
